// Servizio dominio E-COMMERCE (area stakeholder).
// Riusa l'istanza axios centrale (lib/api.js) tramite ./client.
import { api, unwrap } from './client';

const g = (path, params, config) => unwrap(api.get(path, { params, ...(config || {}) }));
const p = (path, body, config) => unwrap(api.post(path, body, config));
const put = (path, body) => unwrap(api.put(path, body));
const del = (path, config) => unwrap(api.delete(path, config));

export const ecommerceService = {
  // Dashboard
  dashboard: (days = 30) => g('/ecommerce/dashboard', { days }),
  seedDemo: () => p('/ecommerce/seed-demo'),

  // Opzioni catalogo (per la UI)
  catalogOptions: () => g('/ecommerce/catalog/options'),

  // Categorie
  listCategories: () => g('/ecommerce/categories'),
  createCategory: (body) => p('/ecommerce/categories', body),
  updateCategory: (id, body) => put(`/ecommerce/categories/${id}`, body),
  deleteCategory: (id, reassignTo) => del(`/ecommerce/categories/${id}`, { params: reassignTo ? { reassign_to: reassignTo } : {} }),

  // Prodotti
  listProducts: (params) => g('/ecommerce/products', params),
  getProduct: (id) => g(`/ecommerce/products/${id}`),
  createProduct: (body) => p('/ecommerce/products', body),
  updateProduct: (id, body) => put(`/ecommerce/products/${id}`, body),
  archiveProduct: (id) => p(`/ecommerce/products/${id}/archive`),
  restoreProduct: (id) => p(`/ecommerce/products/${id}/restore`),
  duplicateProduct: (id) => p(`/ecommerce/products/${id}/duplicate`),
  deleteProduct: (id) => del(`/ecommerce/products/${id}`),
  stripeSync: (id) => p(`/ecommerce/products/${id}/stripe-sync`),
  variantMatrix: (attributes, existing) => p('/ecommerce/products/variant-matrix', { attributes, existing }),
  exportUrl: '/ecommerce/products/export.csv',
  importProducts: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return p('/ecommerce/products/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },

  // Guide taglie
  listSizeGuides: () => g('/ecommerce/size-guides'),
  createSizeGuide: (body) => p('/ecommerce/size-guides', body),
  updateSizeGuide: (id, body) => put(`/ecommerce/size-guides/${id}`, body),
  deleteSizeGuide: (id) => del(`/ecommerce/size-guides/${id}`),

  // Inventario
  invSummary: () => g('/ecommerce/inventory/summary'),
  invItems: (params) => g('/ecommerce/inventory/items', params),
  invAdjust: (body) => p('/ecommerce/inventory/adjust', body),
  invSettings: (body) => p('/ecommerce/inventory/settings', body),
  invMoves: (params) => g('/ecommerce/inventory/moves', params),
  invReservations: (status) => g('/ecommerce/inventory/reservations', status ? { status } : {}),
  invCreateReservation: (body) => p('/ecommerce/inventory/reservations', body),
  invConfirmReservation: (id) => p(`/ecommerce/inventory/reservations/${id}/confirm`),
  invReleaseReservation: (id) => p(`/ecommerce/inventory/reservations/${id}/release`),
  invPurgeExpired: () => p('/ecommerce/inventory/reservations/purge-expired'),
  invAlerts: () => g('/ecommerce/inventory/alerts'),

  // Ordini
  ordStats: () => g('/ecommerce/orders/stats'),
  ordList: (params) => g('/ecommerce/orders', params),
  ordGet: (id) => g(`/ecommerce/orders/${id}`),
  ordCreate: (body) => p('/ecommerce/orders', body),
  ordTransition: (id, body) => p(`/ecommerce/orders/${id}/transition`, body),
  ordRefund: (id, body) => p(`/ecommerce/orders/${id}/refund`, body),
  ordEvent: (id, body) => p(`/ecommerce/orders/${id}/event`, body),
  ordShipping: (id, body) => p(`/ecommerce/orders/${id}/shipping`, body),
  ordInvoice: (id) => p(`/ecommerce/orders/${id}/invoice`),

  // Clienti (anagrafica + vista 360°)
  listCustomers2: (params) => g('/ecommerce/customers', params),
  getCustomer: (id) => g(`/ecommerce/customers/${id}`),
  createCustomer: (body) => p('/ecommerce/customers', body),
  updateCustomer: (id, body) => put(`/ecommerce/customers/${id}`, body),
  deleteCustomer: (id) => del(`/ecommerce/customers/${id}`),
  customerStripeSync: (id) => p(`/ecommerce/customers/${id}/stripe-sync`),
  customerGiftCard: (id, amount) => p(`/ecommerce/customers/${id}/gift-cards`, { amount }),

  // KYC (documenti privati GridFS). Il download avviene via api.get(..., { responseType:'blob' }).
  kycUpload: (id, formData) => p(`/ecommerce/customers/${id}/kyc`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  kycVerify: (id, docId, body) => p(`/ecommerce/customers/${id}/kyc/${docId}/verify`, body),
  kycAiVerify: (id, docId) => p(`/ecommerce/customers/${id}/kyc/${docId}/ai-verify`),
  kycMeta: (id, docId, body) => put(`/ecommerce/customers/${id}/kyc/${docId}/meta`, body),
  kycToken: (id, docId, ttlMinutes = 10) => p(`/ecommerce/customers/${id}/kyc/${docId}/token`, null, { params: { ttl_minutes: ttlMinutes } }),
  kycDelete: (id, docId) => del(`/ecommerce/customers/${id}/kyc/${docId}`),

  // Preventivi (Quotes) — prezzi sempre server-side, conversione in ordine/fattura
  quoteStats: () => g('/ecommerce/quotes/stats'),
  quoteList: (params) => g('/ecommerce/quotes', params),
  quoteGet: (id) => g(`/ecommerce/quotes/${id}`),
  quoteCreate: (body) => p('/ecommerce/quotes', body),
  quoteUpdate: (id, body) => put(`/ecommerce/quotes/${id}`, body),
  quoteSend: (id) => p(`/ecommerce/quotes/${id}/send`),
  quoteView: (id) => p(`/ecommerce/quotes/${id}/view`),
  quoteAccept: (id) => p(`/ecommerce/quotes/${id}/accept`),
  quoteReject: (id, note) => p(`/ecommerce/quotes/${id}/reject`, { note }),
  quoteArchive: (id) => p(`/ecommerce/quotes/${id}/archive`),
  quoteDuplicate: (id) => p(`/ecommerce/quotes/${id}/duplicate`),
  quoteStripeQuote: (id) => p(`/ecommerce/quotes/${id}/stripe-quote`),
  quoteConvert: (id, body) => p(`/ecommerce/quotes/${id}/convert`, body),

  // Spedizioni (Packlink PRO — mock/live). La chiave non viene mai restituita al frontend.
  shipConfig: () => g('/ecommerce/shipping/config'),
  shipConfigUpdate: (body) => put('/ecommerce/shipping/config', body),
  shipConnect: (apiKey) => p('/ecommerce/shipping/connect', { api_key: apiKey }),
  shipDisconnect: () => p('/ecommerce/shipping/disconnect'),
  shipTest: () => g('/ecommerce/shipping/test'),
  shipSenders: () => g('/ecommerce/shipping/senders'),
  shipSenderCreate: (body) => p('/ecommerce/shipping/senders', body),
  shipSenderUpdate: (id, body) => put(`/ecommerce/shipping/senders/${id}`, body),
  shipSenderDelete: (id) => del(`/ecommerce/shipping/senders/${id}`),
  shipServices: () => g('/ecommerce/shipping/services'),
  shipServicesSync: () => p('/ecommerce/shipping/services/sync'),
  shipServiceUpdate: (id, body) => put(`/ecommerce/shipping/services/${id}`, body),
  shipPresets: () => g('/ecommerce/shipping/presets'),
  shipPresetCreate: (body) => p('/ecommerce/shipping/presets', body),
  shipPresetUpdate: (id, body) => put(`/ecommerce/shipping/presets/${id}`, body),
  shipPresetDelete: (id) => del(`/ecommerce/shipping/presets/${id}`),
  shipQuote: (body) => p('/ecommerce/shipping/quote', body),
  shipStats: () => g('/ecommerce/shipping/shipments/stats'),
  shipErrors: () => g('/ecommerce/shipping/errors'),
  shipList: (params) => g('/ecommerce/shipping/shipments', params),
  shipCreate: (body) => p('/ecommerce/shipping/shipments', body),
  shipGet: (id) => g(`/ecommerce/shipping/shipments/${id}`),
  shipBuyLabel: (id) => p(`/ecommerce/shipping/shipments/${id}/label`),
  shipLabelPath: (id) => `/ecommerce/shipping/shipments/${id}/label/file`,
  shipTrack: (id) => p(`/ecommerce/shipping/shipments/${id}/track`),
  shipProblem: (id, message) => p(`/ecommerce/shipping/shipments/${id}/problem`, { message }),
  shipNotify: (id) => p(`/ecommerce/shipping/shipments/${id}/notify`),

  // Resi (RMA)
  retStats: () => g('/ecommerce/returns/stats'),
  retList: (params) => g('/ecommerce/returns', params),
  retCreate: (body) => p('/ecommerce/returns', body),
  retGet: (id) => g(`/ecommerce/returns/${id}`),
  retLabelPath: (id) => `/ecommerce/returns/${id}/label/file`,
  retAction: (id, action, body) => p(`/ecommerce/returns/${id}/${action}`, body || {}),

  // Pagamenti (Payment Service unico — Stripe mock/live)
  payConfig: () => g('/ecommerce/payments/config'),
  payConfigUpdate: (body) => put('/ecommerce/payments/config', body),
  payConnect: (body) => p('/ecommerce/payments/connect', body),
  payDisconnect: () => p('/ecommerce/payments/disconnect'),
  payTest: () => g('/ecommerce/payments/test'),
  payBalance: () => g('/ecommerce/payments/balance'),
  payStats: () => g('/ecommerce/payments/stats'),
  payReconciliation: () => g('/ecommerce/payments/reconciliation'),
  payPayouts: () => g('/ecommerce/payments/payouts'),
  payPayoutCreate: (amount) => p('/ecommerce/payments/payouts', { amount }),
  payTransactions: (params) => g('/ecommerce/payments/transactions', params),
  payTransaction: (id) => g(`/ecommerce/payments/transactions/${id}`),
  payRefund: (body) => p('/ecommerce/payments/refund', body),
  paySimulateFailed: (body) => p('/ecommerce/payments/simulate-failed', body),

  // Fatture e note di credito
  invStats: () => g('/ecommerce/invoices/stats'),
  invList: (params) => g('/ecommerce/invoices', params),
  invCreate: (orderId) => p('/ecommerce/invoices', { order_id: orderId }),
  invGet: (id) => g(`/ecommerce/invoices/${id}`),
  invPdfPath: (id) => `/ecommerce/invoices/${id}/pdf`,
  invVoid: (id) => p(`/ecommerce/invoices/${id}/void`),
  invCreditNote: (id, body) => p(`/ecommerce/invoices/${id}/credit-notes`, body),
  invCnPdfPath: (id, cnId) => `/ecommerce/invoices/${id}/credit-notes/${cnId}/pdf`,

  // Vetrina pubblica (no auth) — negozi e prodotti pubblicati
  publicShops: () => g('/ecommerce/public/shops'),
  publicShop: (ownerId) => g(`/ecommerce/public/shops/${ownerId}`),
  publicShopProduct: (ownerId, slug) => g(`/ecommerce/public/shops/${ownerId}/products/${slug}`),

  // Modulo 8 — Tasse
  taxOptions: () => g('/ecommerce/tax/options'),
  taxSettings: () => g('/ecommerce/tax/settings'),
  taxSettingsUpdate: (body) => put('/ecommerce/tax/settings', body),
  taxPreview: (body) => p('/ecommerce/tax/preview', body),
  taxBackfill: (force = false) => p('/ecommerce/tax/backfill', { force }),

  // Modulo 8 — Profitto
  profitSummary: (params) => g('/ecommerce/profit/summary', params),
  profitBreakdown: (params) => g('/ecommerce/profit/breakdown', params),

  // Modulo 10 — Recensioni (CRM)
  revOptions: () => g('/ecommerce/reviews/options'),
  revStats: (kind) => g('/ecommerce/reviews/stats', { kind }),
  revList: (params) => g('/ecommerce/reviews', params),
  revModerate: (id, action) => p(`/ecommerce/reviews/${id}/moderate`, { action }),
  revReply: (id, text) => p(`/ecommerce/reviews/${id}/reply`, { text }),
  revConnectors: () => g('/ecommerce/reviews/connectors'),
  revConnectorUpdate: (source, body) => put(`/ecommerce/reviews/connectors/${source}`, body),
  revSync: (source) => p(`/ecommerce/reviews/connectors/${source}/sync`),
  revDedupe: () => p('/ecommerce/reviews/dedupe'),

  // Modulo 10 — Recensioni (pubblico)
  pubReviews: (ownerId, productId) => g(`/ecommerce/public/reviews/${ownerId}/${productId}`),
  pubReviewSubmit: (body) => p('/ecommerce/public/reviews/submit', body),
  pubReviewHelpful: (ownerId, id) => p(`/ecommerce/public/reviews/${ownerId}/${id}/helpful`),

  // Modulo 9 — Promozioni / Coupon
  promoOptions: () => g('/ecommerce/promotions/options'),
  promoStats: () => g('/ecommerce/promotions/stats'),
  promoList: (params) => g('/ecommerce/promotions', params),
  promoCreate: (body) => p('/ecommerce/promotions', body),
  promoGet: (id) => g(`/ecommerce/promotions/${id}`),
  promoUpdate: (id, body) => put(`/ecommerce/promotions/${id}`, body),
  promoStatus: (id, status) => p(`/ecommerce/promotions/${id}/status`, { status }),
  promoDelete: (id) => del(`/ecommerce/promotions/${id}`),
  promoValidate: (body) => p('/ecommerce/promotions/validate', body),

  // Modulo 12 — Impostazioni e Integrazioni
  settingsOptions: () => g('/ecommerce/settings/options'),
  settingsGet: () => g('/ecommerce/settings'),
  settingsUpdate: (body) => put('/ecommerce/settings', body),
  settingsAudit: () => g('/ecommerce/settings/audit'),
  settingsIntegrations: () => g('/ecommerce/settings/integrations'),
  postmarkUpdate: (body) => put('/ecommerce/settings/integrations/postmark', body),
  postmarkConnect: (serverToken) => p('/ecommerce/settings/integrations/postmark/connect', { server_token: serverToken }),
  postmarkDisconnect: () => p('/ecommerce/settings/integrations/postmark/disconnect'),
  postmarkTest: () => p('/ecommerce/settings/integrations/postmark/test'),
  kycUpdate: (body) => put('/ecommerce/settings/integrations/kyc', body),
  kycConnect: (apiKey) => p('/ecommerce/settings/integrations/kyc/connect', { api_key: apiKey }),
  kycDisconnect: () => p('/ecommerce/settings/integrations/kyc/disconnect'),
  privacyExport: () => p('/ecommerce/settings/privacy/export'),
  privacyAnonymize: (olderThanDays) => p('/ecommerce/settings/privacy/anonymize', { older_than_days: olderThanDays }),

  // Vetrina editabile (storefront editor)
  storefrontGet: () => g('/ecommerce/storefront'),
  storefrontUpdate: (body) => put('/ecommerce/storefront', body),
};
